<script setup lang="ts">
import { FramingType, type ProductDetails, SalesMethod } from "mws-ts-v2-sdk";

import useEstimatedProductCosts from "~/composables/useEstimatedProductCosts";

type Props = {
  product: ProductDetails;
  isSidebarDetails?: boolean;
};
const { product, isSidebarDetails = false } = defineProps<Props>();

const isBiddingProduct = computed(
  () => product.salesMethod === SalesMethod.Bidding,
);

const {
  data: estimatedProductCosts,
  isLoading,
  providedFilters,
} = useEstimatedProductCosts(() => product);

const { getName: getLocalisedCountryName, loaded } = useLocalisedCountryName();

const shippingCountry = computed(() => {
  if (!providedFilters.value?.destinationCountryCode) {
    return null;
  }

  return getLocalisedCountryName(providedFilters.value?.destinationCountryCode);
});
</script>

<template>
  <ClientOnly>
    <template #fallback>
      <BaseSkeletonLoader
        :height="120"
        width="100%"
      />
    </template>

    <BaseSkeletonLoader
      v-if="isLoading"
      :width="240"
      :height="24"
    />
    <span
      v-else-if="
        estimatedProductCosts?.shippingOfficeName && shippingCountry && loaded
      "
    >
      {{
        $t("product.description.shippingDetails.shippedFromTo", {
          PLACEHOLDER_officeName: `${estimatedProductCosts.shippingOfficeName} ${estimatedProductCosts.shippingOfficeCountryCode ? ` (${estimatedProductCosts.shippingOfficeCountryCode})` : ""}`,
          PLACEHOLDER_deliveryCountry: shippingCountry,
        })
      }}
    </span>
    <span v-else-if="estimatedProductCosts?.shippingOfficeName">
      {{
        $t("product.description.shippingDetails.shippedFrom", {
          PLACEHOLDER_officeName: estimatedProductCosts.shippingOfficeName,
        })
      }}
    </span>

    <ProductShippingEstimatedDelivery
      v-if="isBiddingProduct && !product.finished"
      :class="{ 'text-xs': isSidebarDetails }"
      :estimated-shipping-range="product.estimatedShippingRange"
      :is-frame="product.framingType === FramingType.Framed"
    />

    <ProductShippingEstimatedCosts
      :class="{ 'text-xs mt-px': isSidebarDetails }"
      :product="product"
      :estimated-shipping-cost="
        estimatedProductCosts?.shippingCost ?? undefined
      "
      :is-loading="isLoading"
      :is-sidebar-details="isSidebarDetails"
    />

    <ProductShippingCountrySelector
      v-if="!isSidebarDetails"
      :currency="estimatedProductCosts?.shippingCost?.currency ?? null"
      :destination-country-code="providedFilters.destinationCountryCode"
      :is-loading="isLoading"
    />
  </ClientOnly>
</template>
